import '../assets/css/autocomplete.css'

import { Autocomplete, Box, debounce, Paper } from '@mui/material'
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete/Autocomplete'
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import UserContext from './admin/Contexts/UserContext.tsx'
import { useFetch } from './admin/Hooks/useFetch.ts'
interface Option {
  label: string
  curated?: boolean
}

const SearchFormAutocomplete = (props: {
  value: string
  renderInput: (params: AutocompleteRenderInputParams) => ReactNode
  isLoading: boolean
  setInputValue: (value: string) => void
  autocompleteUrl?: string
  autocompleteUrlAuth?: boolean
  autocompleteItemsKey?: string
  disableClearable?: boolean
  onNewSearch?: (search: string) => void
}) => {
  const { token } = useContext(UserContext)

  const { fetchAdmin } = useFetch(token)
  const [value, setValue] = useState<Option | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<readonly Option[]>([])
  const [valueChanged, setValueChanged] = useState(false)
  const fetchAutocomplete = useMemo(
    () =>
      debounce(async (request: { input: string }, callback: (results?: readonly Option[]) => void) => {
        const result = await fetchAdmin(
          props.autocompleteUrl || '/autocomplete',
          'get',
          { q: request.input },
          { auth: !!props.autocompleteUrlAuth },
        )
        //const result = await fetch(`${host}/${org}/?q=${encodeURIComponent(request.input)}`)
        let data = await result.json()
        if (props.autocompleteItemsKey) {
          data = data[props.autocompleteItemsKey]
        }
        callback(data.map((d: any) => d))
      }, 250),
    [fetchAdmin, props.autocompleteItemsKey, props.autocompleteUrl, props.autocompleteUrlAuth],
  )
  // close autocomplete, when loading starts
  useEffect(() => {
    if (props.isLoading) {
      setOptions([])
    }
  }, [props.isLoading])

  useEffect(() => {
    let active = true

    if (!valueChanged) {
      return undefined
    }
    if (inputValue === '' || props.isLoading) {
      setOptions([])
      return undefined
    }

    fetchAutocomplete({ input: inputValue }, (results?: readonly Option[]) => {
      if (active && results && !props.isLoading) {
        setOptions(results)
      }
    })

    return () => {
      active = false
    }
  }, [valueChanged, value, inputValue, fetchAutocomplete, props.isLoading])

  return (
    <Autocomplete
      groupBy={() => 'Query suggestions:'}
      id="google-map-demo"
      sx={{ width: '100%' }}
      getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
      options={options}
      filterOptions={x => x}
      autoComplete
      clearOnBlur={true}
      clearOnEscape={true}
      value={props.value}
      filterSelectedOptions
      includeInputInList={false}
      disableClearable={props.disableClearable !== false}
      freeSolo
      ListboxProps={{
        sx: { display: options.length ? 'block' : 'none' },
      }} // Hide dropdown if no options
      PaperComponent={(props: any) => (options.length ? <Paper {...props} elevation={5} /> : null)} // If no children (options), do not render the Paper (dropdown)
      onChange={(_event: any, newValue: string | null | Option) => {
        if (typeof newValue === 'string') {
          console.log('IS STRING', newValue)
          return
        }
        setValue(newValue)
        setValueChanged(true)
        if (newValue === null) {
          props.onNewSearch?.('')
        }
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue)
        setValueChanged(true)
        props.setInputValue(newInputValue)
      }}
      renderOption={(props, option) => (
        <Box {...props} key={`${Math.random()}`} component="li">
          {typeof option === 'string' ? option + '(STRING)' : option.label + (option.curated ? ' *' : '')}
        </Box>
      )}
      renderInput={props.renderInput}
    />
  )
}
export default SearchFormAutocomplete
